/**
 * The DIN of the director who signs for a company, for the switcher's D chip.
 *
 * Founder, 10 Oct 2026: "for companies like section 8, pvt, opc we need the DIN
 * number of the authorised person ... generally first director as per the
 * director register, or sometimes he gets changed as per the meeting".
 *
 * So: the director the board authorised, where one is recorded (the
 * `roc_signatory` role, "Every ROC form is signed by a director or company
 * secretary and carries their DIN"), matched to the register by PAN because the
 * role keeps no DIN of its own. Failing that, the first director on the
 * register: the earliest appointed who has not ceased and holds a DIN.
 *
 * The org list reads both tables at sign-in in the request that brings the
 * business's name, so this costs no call. Small on purpose: `useAuth` is in
 * the first load.
 *
 * @since regnos (2026-10-10)
 */

export interface SignerDin {
    din: string;
    /** Whose DIN it is, for the toast: "DIN of Asha Rao". */
    name: string;
    /** True when the board named this signer; false when it is the first director by default. */
    authorised: boolean;
}

interface DirectorRow {
    name?: string | null;
    din?: string | null;
    pan?: string | null;
    designation?: string | null;
    date_of_appointment?: string | null;
    date_of_cessation?: string | null;
}

interface RoleRow {
    role_key?: string | null;
    holder_pan?: string | null;
    holder_name?: string | null;
    vacated_on?: string | null;
}

const DIN_RE = /^\d{8}$/;
const up = (v: unknown) => (typeof v === 'string' ? v.trim().toUpperCase() : '');

/**
 * Undefined when the register was not read (so "not asked" is never "none"),
 * null when it was and no serving director holds a DIN.
 */
export function signerDinOf(directors: unknown, roles: unknown): SignerDin | null | undefined {
    if (!Array.isArray(directors)) return undefined;
    const serving = (directors as DirectorRow[])
        .filter((d) => !d.date_of_cessation && DIN_RE.test((d.din ?? '').trim()));
    if (!serving.length) return null;

    const signer = (Array.isArray(roles) ? roles as RoleRow[] : [])
        .find((r) => r.role_key === 'roc_signatory' && !r.vacated_on && up(r.holder_pan));
    if (signer) {
        const match = serving.find((d) => up(d.pan) === up(signer.holder_pan));
        if (match) return { din: (match.din ?? '').trim(), name: (match.name ?? signer.holder_name ?? '').trim(), authorised: true };
    }

    // A company secretary or CFO may carry a DIN from another board; the first
    // DIRECTOR is the one a register means.
    const directorsOnly = serving.filter((d) => !['ceo', 'cfo', 'company_secretary', 'manager'].includes(d.designation ?? 'director'));
    const first = [...(directorsOnly.length ? directorsOnly : serving)]
        .sort((a, b) => (a.date_of_appointment ?? '9999').localeCompare(b.date_of_appointment ?? '9999'))[0];
    return { din: (first.din ?? '').trim(), name: (first.name ?? '').trim(), authorised: false };
}
